import { FC, useEffect } from 'react';

import {
  Badge,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Eye, Trash2 } from 'react-feather';

import { AlertBar } from '../../components/AlertBar';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useAuth } from '../../context/auth-context';
import { useTable } from '../../util/useTable';
import styled from 'styled-components';
import { useDeleteDevice, useGetDevices } from '../../api-http/devices';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { displayDistance } from '../../util/dateTime';
import { notifyMsg } from '../../configs';
import { useSnack } from '../../util/useSnack';
import { useConfirm } from 'material-ui-confirm';
import { NavLink } from 'react-router-dom';
import { managementTableSx } from '../../components/ManagementPageLayout';

const OnlineStatus = styled(Badge)`
  margin-right: 10px;
  margin-top: -2px;
`;

interface UsersTableProp {
  search: string;
  refresh: boolean;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

export const DevicesTable: FC<UsersTableProp> = ({ refresh, search }) => {
  const { switchedOrgId } = useAuth();
  const { sortOnClick, pagingParams, tableProps } = useTable(true);
  const { sortby, order } = pagingParams;
  const navigate = useNavigate();
  const location = useLocation();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const confirm = useConfirm();

  const { error: deleteError, mutate: removeDevice } = useDeleteDevice({
    onSuccess: () => {
      refetch(), showSuccessSnack(notifyMsg.DEVICE_DELETED_SUCCESSFULLY);
    },
    onError: () =>
      deleteError
        ? showErrSnack(deleteError.msg)
        : showErrSnack(notifyMsg.FAILED_TO_REMOVE_DEVICE),
  });

  const {
    data: devices,
    isLoading,
    refetch,
    isRefetching,
    error,
  } = useGetDevices({ ...pagingParams, search });

  const disableAccess = useIsUserRole([
    UserRole.user,
    UserRole.dataEditor,
    UserRole.layoutEditor,
  ]);

  useEffect(() => {
    refetch();
  }, [refresh, switchedOrgId, search]);

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      removeDevice({ id });
    } catch (e) {}
  };

  return (
    <>
      {(isLoading || isRefetching) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 760 }} aria-label="Devices">
          <TableHead>
            <TableRow>
              <StyledTableCell>Device ID</StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.name || true}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Device Name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">
                <StyledTableSortLabel
                  active={sortby === SortEnum.lastOnline || true}
                  direction={
                    sortby === SortEnum.lastOnline ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.lastOnline)}
                >
                  Last Online
                </StyledTableSortLabel>
              </StyledTableCell>

              <StyledTableCell align="center">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt || true}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices?.data?.map((device) => {
              const { deviceId, name, status, lastOnline, updatedAt } = device;
              return (
                <TableRow key={deviceId} sx={{ height: 56 }}>
                  <TableCell align="left">
                    <NavLink
                      style={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '5px',
                      }}
                      to={`/device/${deviceId}`}
                      state={{
                        search: location?.search,
                      }}
                    >
                      {deviceId}
                    </NavLink>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <NavLink
                      style={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '5px',
                      }}
                      to={`/device/${deviceId}`}
                    >
                      {name}
                    </NavLink>
                  </TableCell>
                  <TableCell align="center" component="th" scope="row">
                    {displayDistance(lastOnline)}
                  </TableCell>
                  <TableCell align="center" component="th" scope="row">
                    {displayDistance(updatedAt)}
                  </TableCell>
                  <TableCell align="center">
                    <OnlineStatus
                      color={status ? 'success' : 'error'}
                      variant="dot"
                    />
                    {status ? 'Online' : 'Offline'}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="View device">
                        <span>
                          <IconButton
                            aria-label={`View ${name}`}
                            onClick={() => navigate(`/device/${deviceId}`)}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Eye size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove device">
                        <span>
                          <IconButton
                            aria-label={`Remove ${name}`}
                            disabled={disableAccess}
                            onClick={() => handleDelete(deviceId)}
                            sx={{
                              ...actionButtonSx,
                              color: '#ef4444',
                              borderColor: '#fecaca',
                              bgcolor: '#fffafa',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={devices?.pagination.total === 0}
          count={devices?.pagination.total || 0}
        />
      </TableContainer>
    </>
  );
};
